import React, { useState, useEffect } from 'react';
import { db, auth } from '../src/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';

/* ─── tiny helpers ─── */
const Field = ({ label, value, onChange, type = 'text', rows }) => (
  <div style={s.field}>
    <label style={s.label}>{label}</label>
    {rows ? (
      <textarea style={{ ...s.input, resize: 'vertical' }} rows={rows} value={value} onChange={onChange} />
    ) : (
      <input style={s.input} type={type} value={value} onChange={onChange} />
    )}
  </div>
);

const Card = ({ title, icon, children, action }) => (
  <div style={s.card}>
    <div style={s.cardHead}>
      <span style={s.cardTitle}>{icon} {title}</span>
      {action}
    </div>
    {children}
  </div>
);

const Btn = ({ onClick, children, variant = 'primary', small }) => (
  <button onClick={onClick} style={{ ...s.btn, ...(variant === 'danger' ? s.btnDanger : {}), ...(small ? s.btnSm : {}) }}>
    {children}
  </button>
);

const Toast = ({ msg, ok }) => msg ? (
  <div style={{ ...s.toast, background: ok ? '#14532d' : '#7f1d1d', color: ok ? '#86efac' : '#fca5a5' }}>
    {ok ? '✓' : '✗'} {msg}
  </div>
) : null;

/* ─── main ─── */
const Dashboard = () => {
  const [tab, setTab] = useState('slides');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // data
  const [about, setAbout] = useState({ title: '', content: '' });
  const [ourStory, setOurStory] = useState({ title: '', content: '' });
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);

  // hero content (stored as slides[0])
  const [hero, setHero] = useState({ title: '', description: '' });

  const notify = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => { if (!u) window.location.href = '/login'; });
    return () => unsub();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [sl, ab, os, sv, pf] = await Promise.all([
          getDoc(doc(db, 'home', 'slides')),
          getDoc(doc(db, 'home', 'about')),
          getDoc(doc(db, 'home', 'ourStory')),
          getDoc(doc(db, 'home', 'services')),
          getDoc(doc(db, 'home', 'portfolio')),
        ]);
        if (sl.exists()) {
          const arr = sl.data().slides || [];
          if (arr[0]) setHero({ title: arr[0].title || '', description: arr[0].description || '' });
        }
        if (ab.exists()) setAbout(ab.data());
        if (os.exists()) setOurStory(os.data());
        if (sv.exists()) setServices(sv.data().services || []);
        if (pf.exists()) setPortfolio(pf.data().projects || []);
      } catch (e) { notify('Failed to load data', false); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const logout = async () => { await signOut(auth); window.location.href = '/login'; };

  /* hero */
  const saveHero = async () => {
    await setDoc(doc(db, 'home', 'slides'), { slides: [{ ...hero }] });
    notify('Hero updated!');
  };

  /* about / story */
  const saveAbout = async () => { await setDoc(doc(db, 'home', 'about'), about); notify('About updated!'); };
  const saveStory = async () => { await setDoc(doc(db, 'home', 'ourStory'), ourStory); notify('Our Story updated!'); };

  /* services inline edit */
  const updateService = (i, key, val) => setServices(services.map((s, idx) => idx === i ? { ...s, [key]: val } : s));
  const saveServices = async () => { await setDoc(doc(db, 'home', 'services'), { services }); notify('Services saved!'); };

  /* portfolio inline edit */
  const updateProject = (i, key, val) => setPortfolio(portfolio.map((p, idx) => idx === i ? { ...p, [key]: val } : p));
  const savePortfolio = async () => { await setDoc(doc(db, 'home', 'portfolio'), { projects: portfolio }); notify('Portfolio saved!'); };

  if (loading) return (
    <div style={{ ...s.page, justifyContent: 'center', alignItems: 'center' }}>
      <div style={s.spinner} />
    </div>
  );

  const TABS = [
    { id: 'slides',    label: '🏠 Hero Content' },
    { id: 'about',     label: '🏢 About' },
    { id: 'story',     label: '📖 Our Story' },
    { id: 'services',  label: '⚙️ Services' },
    { id: 'portfolio', label: '🗂️ Portfolio' },
  ];

  return (
    <div style={s.page}>
      {/* Sidebar */}
      <aside style={s.sidebar}>
        <div style={s.sideTop}>
          <img src="/assets/logo.png" alt="logo" style={{ width: 40, borderRadius: 10 }} />
          <span style={s.sideTitle}>MacroMatrix</span>
        </div>
        <nav style={s.nav}>
          {TABS.map(t => (
            <button
              key={t.id}
              style={{ ...s.navItem, ...(tab === t.id ? s.navActive : {}) }}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} style={s.logoutBtn}>🚪 Sign Out</button>
      </aside>

      {/* Main */}
      <main style={s.main}>
        <div style={s.topbar}>
          <h1 style={s.pageTitle}>{TABS.find(t => t.id === tab)?.label}</h1>
          <span style={s.badge}>Admin</span>
        </div>

        {toast && <Toast msg={toast.msg} ok={toast.ok} />}

        {/* ── HERO CONTENT ── */}
        {tab === 'slides' && (
          <Card title="Hero Content" icon="🏠" action={<Btn small onClick={saveHero}>💾 Save</Btn>}>
            <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
              This is the main headline and description shown on the home page with the typing animation.
            </p>
            <Field
              label="Headline (typing animation text)"
              value={hero.title}
              onChange={e => setHero({ ...hero, title: e.target.value })}
            />
            <Field
              label="Description"
              value={hero.description}
              rows={3}
              onChange={e => setHero({ ...hero, description: e.target.value })}
            />
          </Card>
        )}

        {/* ── ABOUT ── */}
        {tab === 'about' && (
          <Card title="About Section" icon="🏢" action={<Btn small onClick={saveAbout}>Save</Btn>}>
            <Field label="Title" value={about.title} onChange={e => setAbout({ ...about, title: e.target.value })} />
            <Field label="Content" value={about.content} rows={6} onChange={e => setAbout({ ...about, content: e.target.value })} />
          </Card>
        )}

        {/* ── OUR STORY ── */}
        {tab === 'story' && (
          <Card title="Our Story" icon="📖" action={<Btn small onClick={saveStory}>Save</Btn>}>
            <Field label="Title" value={ourStory.title} onChange={e => setOurStory({ ...ourStory, title: e.target.value })} />
            <Field label="Content" value={ourStory.content} rows={6} onChange={e => setOurStory({ ...ourStory, content: e.target.value })} />
          </Card>
        )}

        {/* ── SERVICES ── */}
        {tab === 'services' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <Btn onClick={saveServices}>💾 Save All Services</Btn>
            </div>
            {services.map((sv, i) => (
              <Card key={i} title={`Service ${i + 1}`} icon="⚙️">
                <Field label="Title" value={sv.title || ''} onChange={e => updateService(i, 'title', e.target.value)} />
                <Field label="Description" value={sv.description || ''} rows={3} onChange={e => updateService(i, 'description', e.target.value)} />
              </Card>
            ))}
            {services.length === 0 && <div style={s.empty}>No services found in Firestore.</div>}
          </>
        )}

        {/* ── PORTFOLIO ── */}
        {tab === 'portfolio' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <Btn onClick={savePortfolio}>💾 Save All Projects</Btn>
            </div>
            {portfolio.map((pj, i) => (
              <Card key={i} title={pj.title || `Project ${i + 1}`} icon="🗂️">
                <Field label="Title" value={pj.title || ''} onChange={e => updateProject(i, 'title', e.target.value)} />
                <Field label="Category" value={pj.category || ''} onChange={e => updateProject(i, 'category', e.target.value)} />
                <Field label="Description" value={pj.description || ''} rows={3} onChange={e => updateProject(i, 'description', e.target.value)} />
              </Card>
            ))}
            {portfolio.length === 0 && <div style={s.empty}>No projects found in Firestore.</div>}
          </>
        )}
      </main>
    </div>
  );
};

/* ─── styles ─── */
const s = {
  page: {
    display: 'flex',
    minHeight: '100vh',
    background: '#0f172a',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    color: '#f1f5f9',
  },
  sidebar: {
    width: 240,
    minHeight: '100vh',
    background: '#1e293b',
    borderRight: '1px solid #334155',
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 16px',
    gap: 8,
    position: 'sticky',
    top: 0,
    alignSelf: 'flex-start',
  },
  sideTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '0 8px 24px',
    borderBottom: '1px solid #334155',
    marginBottom: 8,
  },
  sideTitle: { fontWeight: 700, fontSize: 16, color: '#f1f5f9' },
  nav: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1 },
  navItem: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    textAlign: 'left',
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'all 0.2s',
  },
  navActive: { background: '#0f172a', color: '#14c9b2' },
  logoutBtn: {
    background: 'none',
    border: '1px solid #334155',
    color: '#64748b',
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'left',
    marginTop: 'auto',
  },
  main: { flex: 1, padding: '32px 40px', maxWidth: 900 },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 28,
  },
  pageTitle: { margin: 0, fontSize: 24, fontWeight: 800 },
  badge: {
    background: 'linear-gradient(135deg,#14c9b2,#7c5cbf)',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
  },
  toast: {
    padding: '12px 18px',
    borderRadius: 12,
    marginBottom: 20,
    fontSize: 14,
    fontWeight: 600,
  },
  card: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 16,
    padding: '24px',
    marginBottom: 20,
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  cardTitle: { fontWeight: 700, fontSize: 16, color: '#f1f5f9' },
  field: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 },
  label: { color: '#94a3b8', fontSize: 13, fontWeight: 600 },
  input: {
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #334155',
    background: '#0f172a',
    color: '#f1f5f9',
    fontSize: 14,
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  },
  btn: {
    padding: '10px 20px',
    borderRadius: 10,
    border: 'none',
    background: 'linear-gradient(135deg,#14c9b2,#7c5cbf)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
  btnDanger: { background: '#7f1d1d', color: '#fca5a5' },
  btnSm: { padding: '6px 14px', fontSize: 13 },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  listItem: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  listTitle: { fontWeight: 700, fontSize: 15, marginBottom: 4 },
  listSub: { color: '#64748b', fontSize: 13 },
  empty: { color: '#475569', textAlign: 'center', padding: 40, fontSize: 14 },
  spinner: {
    width: 40, height: 40,
    border: '3px solid #334155',
    borderTop: '3px solid #14c9b2',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

export default Dashboard;
